import { ChromeEventPublisher } from '../events/eventPublisher';
import { PromptService } from '../../domain/services/promptService';
import { FolderService } from '../../domain/services/folderService';
import { RepositoryFactory } from '../storage/repositoryFactory';

export class ServiceFactory {
  private static instance: ServiceFactory;
  private promptService: PromptService;
  private folderService: FolderService;

  private constructor(private readonly repositoryFactory: RepositoryFactory) {
    const promptRepository = repositoryFactory.getPromptRepository();
    const folderRepository = repositoryFactory.getFolderRepository();
    const hierarchyBuilder = repositoryFactory.getHierarchyBuilder();
    const specialFolderFactory = repositoryFactory.getSpecialFolderFactory();
    const eventPublisher = repositoryFactory.getEventPublisher();

    this.promptService = new PromptService(promptRepository, folderRepository, eventPublisher);

    this.folderService = new FolderService(
      folderRepository,
      promptRepository,
      hierarchyBuilder,
      specialFolderFactory,
      eventPublisher
    );
  }

  static initialize(repositoryFactory: RepositoryFactory): ServiceFactory {
    if (!ServiceFactory.instance) {
      ServiceFactory.instance = new ServiceFactory(repositoryFactory);
    }
    return ServiceFactory.instance;
  }

  static getInstance(): ServiceFactory {
    if (!ServiceFactory.instance) {
      throw new Error('ServiceFactory not initialized');
    }
    return ServiceFactory.instance;
  }

  getPromptService(): PromptService {
    return this.promptService;
  }

  getFolderService(): FolderService {
    return this.folderService;
  }
}

export class ServiceProvider {
  private static instance: ServiceProvider;
  private eventPublisher: ChromeEventPublisher;
  private repositoryFactory: RepositoryFactory;
  private serviceFactory: ServiceFactory;

  private constructor() {
    this.eventPublisher = new ChromeEventPublisher();
    this.repositoryFactory = RepositoryFactory.initialize(this.eventPublisher);
    this.serviceFactory = ServiceFactory.initialize(this.repositoryFactory);
  }

  static initialize(): ServiceProvider {
    if (!ServiceProvider.instance) {
      ServiceProvider.instance = new ServiceProvider();
    }
    return ServiceProvider.instance;
  }

  static getInstance(): ServiceProvider {
    // Lazily initialize instead of throwing: initialize() is idempotent, and
    // not every entry point calls it first (the side panel didn't, so every
    // side-panel prompt delete threw "ServiceProvider not initialized").
    if (!ServiceProvider.instance) {
      ServiceProvider.instance = new ServiceProvider();
    }
    return ServiceProvider.instance;
  }

  getEventPublisher(): ChromeEventPublisher {
    return this.eventPublisher;
  }

  getRepositoryFactory(): RepositoryFactory {
    return this.repositoryFactory;
  }

  getServiceFactory(): ServiceFactory {
    return this.serviceFactory;
  }

  getPromptService(): PromptService {
    return this.serviceFactory.getPromptService();
  }

  getFolderService(): FolderService {
    return this.serviceFactory.getFolderService();
  }
}
